import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, Check, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";

const RULES = [
  ["12+ characters", (p) => p.length >= 12],
  ["Uppercase letter", (p) => /[A-Z]/.test(p)],
  ["Lowercase letter", (p) => /[a-z]/.test(p)],
  ["Number", (p) => /\d/.test(p)],
  ["Symbol (e.g. ! ? #)", (p) => /[^A-Za-z0-9]/.test(p)],
];

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const forced = !!user?.must_change_password;
  const local = (user?.email || "").split("@")[0].toLowerCase();
  const checks = useMemo(() => RULES.map(([l, fn]) => [l, fn(f.new_password)]).concat([["Does not contain your email name", !(local.length >= 4 && f.new_password.toLowerCase().includes(local))], ["Differs from current password", !!f.new_password && f.new_password !== f.current_password]]), [f, local]);
  const score = checks.filter(([, ok]) => ok).length;
  const allOk = score === checks.length && f.confirm === f.new_password;

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) return toast.error("Please satisfy every rule and confirm the new password.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      if (data.access_token) localStorage.setItem("dk_token", data.access_token);
      await refresh();
      toast.success("Password updated");
      nav("/admin", { replace: true });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#155e3f] text-white flex items-center justify-center p-6" data-testid="change-password-page">
      <form onSubmit={submit} className="w-full max-w-lg bg-[#155e3f] rounded-2xl p-8 border border-white/10 space-y-5">
        <div className="flex items-start gap-3">
          <ShieldAlert className="text-[#d6aa43] flex-shrink-0" size={28} />
          <div>
            <h1 className="font-serif text-2xl font-bold">{forced ? "Set a new password to continue" : "Change password"}</h1>
            {forced && <p className="text-sm text-white mt-1" data-testid="forced-notice">Your account requires a password rotation before you can use the staff portal. This protects the Foundation's records.</p>}
          </div>
        </div>
        {[["current_password", "Current password"], ["new_password", "New password"], ["confirm", "Confirm new password"]].map(([k, l]) => (
          <div key={k}><label className="text-xs uppercase tracking-[0.18em] text-white font-semibold mb-2 block">{l}</label>
            <input type="password" required autoComplete={k === "current_password" ? "current-password" : "new-password"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#155e3f] border border-white/15 focus:border-[#d6aa43] outline-none" data-testid={`pw-${k}`} /></div>
        ))}
        <div className="h-1.5 rounded bg-white/10 overflow-hidden"><div className={`h-full transition-all ${score >= checks.length ? "bg-green-500" : score >= 4 ? "bg-[#d6aa43]" : "bg-red-500"}`} style={{ width: `${(score / checks.length) * 100}%` }} data-testid="pw-strength" /></div>
        <ul className="grid sm:grid-cols-2 gap-1 text-xs" data-testid="pw-rules">{checks.map(([l, ok]) => <li key={l} className={`flex items-center gap-1 ${ok ? "text-green-400" : "text-white"}`}>{ok ? <Check size={12} /> : <X size={12} />} {l}</li>)}</ul>
        {f.confirm && f.confirm !== f.new_password && <p className="text-xs text-red-400" data-testid="pw-mismatch">Passwords do not match.</p>}
        <button type="submit" disabled={busy || !allOk} className="dk-btn-primary w-full justify-center disabled:opacity-40" data-testid="pw-submit"><KeyRound size={16} /> {busy ? "Saving…" : "Save new password"}</button>
        <button type="button" onClick={async () => { await logout(); nav("/admin/login"); }} className="w-full text-xs text-white underline">Sign out instead</button>
      </form>
    </div>
  );
}
